import React from "react";
import ScriptRow from "../ScriptRow/ScriptRow";
import AudioIcon from "../../../assets/icons/audio-purple-bg.svg";
import DoubleFilesIcon from "../../../assets/icons/double-files-purple-bg.svg";
import { useTranslation } from "react-i18next";
import EditScriptActionsModal from "../modals/EditScriptActionsModal/EditScriptActionsModal";
import ConfirmDeleteScriptModal from "../modals/ConfirmDeleteScriptModal/ConfirmDeleteScriptModal";
import { useNavigate } from "react-router-dom";
import DetailChatScriptModal from "../modals/DetailChatScriptModal/DetailChatScriptModal";
import Loader from "../../loader/Loader";
import { chatScriptService } from "../../../services/chat-script.service";
import { message } from "antd";
import { useAppDispatch } from "../../../redux/hooks";
import { removeScriptFromCache } from "../../../redux/chatScript/slices/scriptSlice";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import "./ScriptList.less";

export type ScriptItem = {
  id: string;
  title: string;
  message: string;
  price?: number | null;
  type: "script" | "voice";
  scriptType?: string;
  category?: string;
};

export type ScriptListProps = {
  items: ScriptItem[];
  loading?: boolean;
  listRef?: React.RefObject<HTMLDivElement | null>;
  sentinelRef?: React.RefObject<HTMLDivElement | null>;
  pickerMode?: boolean;
  onSend?: (item: ScriptItem) => void;
  alreadySentIds?: string[];
};

const ScriptList: React.FC<ScriptListProps> = ({ items, loading = false, listRef, sentinelRef, pickerMode = false, onSend, alreadySentIds = [] }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { currentUser } = useFetchUserInfos();
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState<string | null>(null);
  const [showActions, setShowActions] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<ScriptItem | null>(null);
  const [actionItem, setActionItem] = React.useState<ScriptItem | null>(null);
  const [listItems, setListItems] = React.useState<ScriptItem[]>(items);

  React.useEffect(() => {
    setListItems(items);
  }, [items]);

  const handleEditClick = (item: ScriptItem) => {
    setSelected(item.title);
    setActionItem(item);
    setShowActions(true);
  };

  const closeActions = () => setShowActions(false);
  const closeConfirm = () => setShowConfirm(false);

  const requestDelete = () => {
    setShowActions(false);
    setShowConfirm(true);
  };

  const confirmDelete = async (_name: string) => {
    try {
      if (!actionItem) return setShowConfirm(false);
      const ok = await chatScriptService.deleteScript(actionItem.id);
      if (ok) {
        setListItems((prev) => prev.filter((it) => it.id !== actionItem.id));
        setSelectedItem((prev) => (prev && prev.id === actionItem.id ? null : prev));
        if (currentUser?.id) {
          dispatch(removeScriptFromCache({ creatorId: currentUser.id, id: actionItem.id }));
        }
        message.success(t("script-deleted-successfully"));
      }
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 403) {
        message.error(t("not-authorized-to-delete-script"));
      } else {
        console.error("Failed to delete script", e);
        message.error(t("back-error-message"));
      }
    } finally {
      setShowConfirm(false);
      setShowActions(false);
      setActionItem(null);
      setSelected(null);
    }
  };

  const openDetail = (item: ScriptItem) => {
    setSelectedItem(item);
    setShowDetail(true);
  };

  const closeDetail = () => {
    setShowDetail(false);
    setSelectedItem(null);
  };
  return (
    <div className="script-list-card">
      <div className="script-list" role="list" ref={listRef as unknown as React.RefObject<HTMLDivElement>}>
        {loading ? (
          <div className="loader-script-wrapper">
            <Loader />
          </div>
        ) : listItems.length === 0 ? (
          <div className="script-list-empty" role="note" aria-live="polite">
            {t("no-scripts-in-category")}
          </div>
        ) : (
          <>
            {listItems.map((it, idx) => (
              <React.Fragment key={it.id}>
                <div className="script-list-item" role="listitem">
                  <ScriptRow
                    title={it.title}
                    message={it.message}
                    price={it.price}
                    iconSrc={it.type === "voice" ? AudioIcon : DoubleFilesIcon}
                    onEdit={() => handleEditClick(it)}
                    onClick={() => openDetail(it)}
                    showSendButton={pickerMode}
                    alreadySent={pickerMode ? alreadySentIds.includes(it.id) : false}
                    onSend={() => {
                      if (pickerMode) {
                        onSend && onSend(it);
                      }
                    }}
                  />
                </div>
                {pickerMode && idx < listItems.length - 1 && <div className="script-list-divider" aria-hidden="true" />}
              </React.Fragment>
            ))}
            <div ref={sentinelRef as unknown as React.RefObject<HTMLDivElement>} style={{ height: 1 }} />
          </>
        )}
      </div>
      {!pickerMode && showActions && selected && (
        <EditScriptActionsModal
          name={selected}
          onClose={closeActions}
          onRequestDelete={() => requestDelete()}
          onRequestEditScript={() => {
            // Get the current location to determine where we're editing from
            const currentPath = window.location.pathname;
            const fromSettings = currentPath.includes('/common/setting') || currentPath.includes('/settings');
            
            if (actionItem) {
              navigate(`/creator/chat-scripts/update?id=${encodeURIComponent(actionItem.id)}&name=${encodeURIComponent(selected)}${fromSettings ? '&from=settings' : ''}`);
            } else {
              navigate(`/creator/chat-scripts/update?name=${encodeURIComponent(selected)}${fromSettings ? '&from=settings' : ''}`);
            }
          }}
          onRequestReorder={() => {
            navigate("/creator/chat-scripts/order");
          }}
        />
      )}
      {!pickerMode && showConfirm && selected && <ConfirmDeleteScriptModal name={selected} onCancel={closeConfirm} onConfirm={confirmDelete} />}
      {showDetail && selectedItem && <DetailChatScriptModal id={selectedItem.id} onClose={closeDetail} />}
    </div>
  );
};

export default ScriptList;
